import { useContext, useEffect, useState } from 'react'
import { Box, Button } from '@mui/material'
import { IRole, IUser } from '../../../../shared/types/api/user.type'
import { useToast } from '../../../contexts/toast.context'
import { usePermission } from '../../../hooks/usePermission'
import TableComponent from '../../shared/Table'
import { AppConfigContext } from '../../../contexts/app-config.context'
import { UsersTableConfigs } from '../../../utils/constants'
import UserViewModal from '../Modals/UserViewModal'
import RoleModal from '../Modals/RoleModal'
import { getUsers, deleteUser, getRolesAndPermissions } from '../../../services/user.service'

import styles from './styles.module.scss'

const Users = () => {
  const [users, setUsers] = useState<IUser[]>([])
  const [isViewMode, setIsViewMode] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null)
  const [isManagingRoles, setIsManagingRoles] = useState(false)
  const [isManagingTiers, setIsManagingTiers] = useState(false)
  const [rolesWithPermissions, setRolesWithPermissions] = useState<IRole[]>([])

  const hasWriteAccess = usePermission('write', 'users')
  const hasTierWriteAccess = usePermission('write', 'tiers')

  const { showToast } = useToast()

  const { isLightMode } = useContext(AppConfigContext)

  const getRoles = async () => {
    const body = await getRolesAndPermissions()
    setRolesWithPermissions(body.data || [])
  }

  const handleView = (user: IUser) => {
    setIsViewMode(true)
    setSelectedUser(user)
  }

  const handleClose = () => {
    setIsCreating(false)
    setIsViewMode(false)
  }

  const fetchUsers = async () => {
    const data = await getUsers()

    if (data.success && data.data) {
      const users = data.data.map((user) => ({
        ...user,
        roleNames: user.roles.map((role) => role.name).join(', '),
      }))
      setUsers(users)
    }
  }

  const handleCreate = () => {
    setSelectedUser({
      id: '',
      email: '',
      roles: [],
      username: '',
      tiers_by_level: {},
      created_at: JSON.stringify(new Date()),
    })
    setIsCreating(true)
  }

  const handleDelete = async (userId: string) => {
    const data = await deleteUser(userId)

    if (data.success) {
      showToast('success', 'User deleted successfully')
      setUsers(users.filter((user) => user.id !== userId))
    }
  }

  useEffect(() => {
    getRoles()
    fetchUsers()
  }, [])

  return (
    <Box className={styles.users} style={{ backgroundColor: isLightMode ? 'white' : '#13222e' }}>
      <TableComponent
        data={users}
        configs={UsersTableConfigs}
        addBtnText="Add User"
        handleView={handleView}
        isLightMode={isLightMode}
        handleCreate={handleCreate}
        handleDelete={async (id: number) => await handleDelete(String(id))}
        searchMaxWidth={true}
        hasWriteAccess={!!hasWriteAccess}
        preface={
          <Box className={styles.permissionActions}>
            <Button onClick={() => setIsManagingRoles(true)}>Manage Roles</Button>
            {hasTierWriteAccess && <Button onClick={() => setIsManagingTiers(true)}>Manage Tiers</Button>}
          </Box>
        }
      />
      {selectedUser && (
        <UserViewModal
          user={selectedUser}
          open={isCreating || isViewMode}
          fields={UsersTableConfigs.items}
          onClose={handleClose}
          fetchUsers={fetchUsers}
          isLightMode={isLightMode}
          globalRolesWithPermissions={rolesWithPermissions}
        />
      )}
      {isManagingRoles && (
        <RoleModal
          open={isManagingRoles}
          isLightMode={isLightMode}
          rolesWithPermissions={rolesWithPermissions}
          setRolesWithPermissions={setRolesWithPermissions}
          onClose={() => {
            setIsManagingRoles(false)
          }}
        />
      )}
    </Box>
  )
}

export default Users
